import type { PublishQuestionnaireTemplateVersionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface PublishQuestionnaireTemplateVersionInput {
  QuestionnaireTemplateId: string;
  QuestionnaireTemplateVersionId: string;
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation
 * response shape so consumers branching on the result do not need
 * to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): PublishQuestionnaireTemplateVersionMutation => ({
  publishQuestionnaireTemplateVersion: {
    __typename: 'GenericMutationOutput',
    affected_rows: data.affected_rows,
  },
});

export const usePublishQuestionnaireTemplateVersionTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.questionnaireTemplateVersion.publish.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.questionnaireTemplateVersion.pathKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.questionnaireTemplate.pathKey(),
        });
      },
    }),
  });

  return {
    publishQuestionnaireTemplateVersion: async (
      input: PublishQuestionnaireTemplateVersionInput
    ): Promise<PublishQuestionnaireTemplateVersionMutation> => {
      const result = await mutation.mutateAsync({
        QuestionnaireTemplateId: input.QuestionnaireTemplateId,
        QuestionnaireTemplateVersionId: input.QuestionnaireTemplateVersionId,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
